/**
 * App Router — React Router 7 with lazy-loaded page components and context-driven route guards.
 * Arabic default (dir="rtl", lang="ar").
 *
 * Architecture:
 * - Route guards (GuestGuard, AuthGuard, AdminGuardLayout) are LAYOUT COMPONENTS.
 *   They use <Outlet /> to render matched child routes.
 * - Shell components (AppShell, AdminShell) are rendered ONCE by their guard via <Outlet>.
 * - PublisherGuardPage is NOT a layout route — it's inline redirect logic rendered via Outlet.
 *
 * Layout persistence: AppShell/AdminShell stay mounted via their guard's <Outlet>.
 *
 * DRY: one Spinner source, one LoadingScreen, shells mounted at route level.
 */
import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router";
import { AdminShell } from "@/features/admin/components/AdminShell";
import { AppShell } from "@/app/AppShell";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { getEnumsQueryOptions } from "@/shared/lib/enumsQueryOptions";
import { countriesQueryOptions } from "@/shared/lib/countriesQueryOptions";
import { queryClient } from "@/shared/lib/queryClient";
import { AuthProvider } from "@/app/providers/AuthProvider";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuthContext } from "@/shared/hooks/useAuthContext";
import { useSubscription } from "@/features/pricing/hooks/useSubscription";
import { ReadOnlyProvider } from "@/app/providers/ReadOnlyProvider";
import { RootErrorElement } from "@/shared/components/RootErrorElement";
import { PageSpinner } from "@/shared/components";
import { DirectionProvider } from "@/app/DirectionProvider";
// Error pages are eager (not lazy): RootErrorBoundary/RootErrorElement import
// them statically, so Rollup cannot code-split them anyway, and an error chunk
// fetched mid-failure is unreliable. Keeping them in the static graph.
import { NotFoundPage } from "@/features/errors/components/NotFoundPage";
import { ErrorPage } from "@/features/errors/components/ErrorPage";

/** Lazy-loaded pages — code-split per route */
const LoginPage = lazy(() => import("@/features/auth/components/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/features/auth/components/RegisterPage").then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("@/features/auth/components/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/features/auth/components/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const EmailVerificationPage = lazy(() => import("@/features/auth/components/EmailVerificationPage").then(m => ({ default: m.EmailVerificationPage })));
const AdminDashboardPage = lazy(() => import("@/features/admin/components/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const PublishersPage = lazy(() => import("@/features/admin/components/PublishersPage").then(m => ({ default: m.PublishersPage })));
const AdminTitlesPage = lazy(() => import("@/features/admin/components/AdminTitlesPage").then(m => ({ default: m.AdminTitlesPage })));
const SettingsPage = lazy(() => import("@/features/settings").then(m => ({ default: m.SettingsPage })));
const PublisherDashboardPage = lazy(() => import("@/features/dashboard/components/PublisherDashboardPage").then(m => ({ default: m.PublisherDashboardPage })));
const PublicLegalPage = lazy(() => import("@/features/legal/components/PublicLegalPage").then(m => ({ default: m.PublicLegalPage })));
const PricingPage = lazy(() => import("@/features/pricing").then(m => ({ default: m.PricingPage })));
const CheckoutCancelledPage = lazy(() => import("@/features/pricing").then(m => ({ default: m.CheckoutCancelledPage })));
const CheckoutSuccessPage = lazy(() => import("@/features/pricing").then(m => ({ default: m.CheckoutSuccessPage })));
const BillingDashboardPage = lazy(() => import("@/features/pricing").then(m => ({ default: m.BillingDashboardPage })));
const ChangePasswordPage = lazy(() => import("@/features/auth/components/ChangePasswordForm/ChangePasswordPage").then(m => ({ default: m.ChangePasswordPage })));
const TitleListPage = lazy(() => import("@/features/titles/components/TitleListPage").then(m => ({ default: m.TitleListPage })));
const TitleWizardPage = lazy(() => import("@/features/titles/components/TitleWizardPage").then(m => ({ default: m.TitleWizardPage })));
const NotificationsPage = lazy(() => import("@/features/notifications/components/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const ServiceRequestsPage = lazy(() => import("@/features/services").then(m => ({ default: m.ServiceRequestsPage })));
const AdminProfilePage = lazy(() => import("@/features/admin/components/AdminProfilePage").then(m => ({ default: m.AdminProfilePage })));
const PublisherProfilePage = lazy(() => import("@/features/dashboard/components/PublisherProfilePage").then(m => ({ default: m.PublisherProfilePage })));
const TitleDetailPage = lazy(() => import("@/features/admin/components/TitleDetailPage").then(m => ({ default: m.TitleDetailPage })));
const RevisionReviewPage = lazy(() => import("@/features/admin/components/revision-review/RevisionReviewPage").then(m => ({ default: m.RevisionReviewPage })));
const PublisherTitleDetailPage = lazy(() => import("@/features/titles/components/TitleDetailPage").then(m => ({ default: m.TitleDetailPage })));
const PublisherTitleRevisionPage = lazy(() => import("@/features/titles/components/TitleRevisionPage").then(m => ({ default: m.TitleRevisionPage })));
const PublisherDetailPage = lazy(() => import("@/features/admin/components/PublisherDetailPage").then(m => ({ default: m.PublisherDetailPage })));
const EnterpriseProvisionPage = lazy(() => import("@/features/admin/components/EnterpriseProvisionPage").then(m => ({ default: m.EnterpriseProvisionPage })));
const AdminLegalListPage = lazy(() => import("@/features/admin/components/AdminLegalListPage").then(m => ({ default: m.AdminLegalListPage })));
const AdminLegalEditPage = lazy(() => import("@/features/admin/components/AdminLegalEditPage").then(m => ({ default: m.AdminLegalEditPage })));

/** Full-screen loading screen — used during auth bootstrap. Fills viewport, no shell. */
function LoadingScreen() {
  return <PageSpinner fullScreen />;
}

/**
 * PublicPageLayout — bare layout for fully standalone public pages (legal reader etc.).
 * No AppShell chrome, no padding, no sidebar — just direction context.
 */
function PublicPageLayout() {
  return (
    <DirectionProvider>
      {/* Standalone pages have no shell, so they carry their own Suspense
          boundary for lazy route chunks (legal reader, pricing, checkout). */}
      <Suspense fallback={<PageSpinner fullScreen />}>
        <Outlet />
      </Suspense>
    </DirectionProvider>
  );
}

/**
 * GuestGuard — auth-aware layout for guest routes.
 * Redirects authenticated users to /dashboard or /admin.
 * Renders AppShell with child routes via <Outlet />.
 */
function GuestGuard() {
  const auth = useAuthContext();
  if (auth.status === "loading") return <LoadingScreen />;
  if (auth.status === "authenticated") {
    return <Navigate to={auth.isAdmin ? "/admin" : "/dashboard"} replace />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * AuthGuard — auth-aware layout for protected publisher routes.
 * Redirects unauthenticated users to /login.
 * Renders AppShell with child routes via <Outlet />.
 * Non-admin users see publisher portal; admins are redirected to /admin.
 */
function AuthGuard() {
  const auth = useAuthContext();
  const queryClient = useQueryClient();
  const isPublisher = auth.status === "authenticated" && !auth.isAdmin;

  // Subscription access state — only fetched for publisher sessions. Called
  // unconditionally (Rules of Hooks); `enabled: false` skips the fetch for
  // guests/admins, who return before the access-state gate below anyway.
  const subscription = useSubscription({ enabled: isPublisher });

  // Warm the localized enum catalog + countries list once authenticated so the
  // title wizard's dropdowns and profile/country pickers render instantly.
  // Both queries are long-cached (enums 1h, countries Infinity), so this is a
  // one-shot warm — subsequent reads are served from cache.
  useEffect(() => {
    if (isPublisher) {
      void queryClient.prefetchQuery(getEnumsQueryOptions());
      void queryClient.prefetchQuery(countriesQueryOptions());
    }
    // Depend on the derived primitive, not the `auth` object — its identity
    // changes every render, which would re-run this one-shot prefetch needlessly.
  }, [isPublisher, queryClient]);

  if (auth.status === "loading") return <LoadingScreen />;
  if (auth.status === "unauthenticated") return <Navigate to="/login" replace />;
  if (auth.isAdmin) return <Navigate to="/admin/dashboard" replace />;

  // Gate publisher access on subscription state.
  // never_subscribed → first-timers never see the dashboard, sent to /pricing.
  // read_only → lapsed: dashboard renders, wrapped so read-only mode is active.
  // active → full access.
  if (subscription.isLoading || subscription.data === undefined) return <LoadingScreen />;
  if (subscription.data.accessState === "never_subscribed") {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <ReadOnlyProvider value={subscription.data.accessState === "read_only"}>
      <AppShell>
        <Outlet />
      </AppShell>
    </ReadOnlyProvider>
  );
}

/**
 * AdminGuardLayout — wraps AdminShell as a route element.
 * Checks auth + admin status, redirects non-admins to /dashboard.
 */
function AdminGuardLayout() {
  const auth = useAuthContext();
  if (auth.status === "loading") return <LoadingScreen />;
  if (auth.status === "unauthenticated") return <Navigate to="/login" replace />;
  if (!auth.isAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AdminShell />
  );
}

/**
 * SPA base path, derived from Vite's `base` (set via VITE_BASE_PATH at build).
 * Local dev + Cloudflare root → '/'; WordPress subdirectory build → '/portal'.
 * React Router prepends this to every route path, <Link>, <Navigate> and
 * useNavigate transparently, so the route table below stays root-relative.
 * import.meta.env.BASE_URL carries a trailing slash; React Router wants
 * '/portal' not '/portal/', so strip it. Empty (root) collapses back to '/'.
 */
const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

const router = createBrowserRouter(
  [
    // ── Guest routes — Login/Register/ForgotPassword (no shell) ────────────────
    {
      element: <GuestGuard />,
      children: [
        { path: "/login",           element: <LoginPage /> },
        { path: "/register",        element: <RegisterPage /> },
        { path: "/forgot-password", element: <ForgotPasswordPage /> },
      ],
    },

    // ── Semi-public routes inside AppShell (reset-password, verify-email) ──
    {
      element: <AppShell />,
      children: [
        { path: "/reset-password",  element: <ResetPasswordPage /> },
        { path: "/verify-email",    element: <EmailVerificationPage /> },
      ],
    },

    // ── Fully standalone public pages — no AppShell, no chrome ──
    {
      element: <PublicPageLayout />,
      children: [
        { path: "/legal/:docType",  element: <PublicLegalPage /> },
        { path: "/pricing",         element: <PricingPage /> },
        { path: "/billing/success", element: <CheckoutSuccessPage /> },
        { path: "/billing/cancel",  element: <CheckoutCancelledPage /> },
      ],
    },

    // ── Admin routes — AdminShell persists across all /admin/* pages ────────────
    {
      path: "admin",
      element: <AdminGuardLayout />,
      children: [
        { index: true,          element: <Navigate to="/admin/dashboard" replace /> },
        { path: "dashboard",     element: <AdminDashboardPage /> },
        { path: "titles",        element: <AdminTitlesPage /> },
        { path: "titles/:id",    element: <TitleDetailPage /> },
        { path: "titles/:id/revision", element: <RevisionReviewPage /> },
        { path: "publishers",    element: <PublishersPage /> },
        { path: "publishers/:id", element: <PublisherDetailPage /> },
        { path: "enterprise",    element: <EnterpriseProvisionPage /> },
        { path: "services",      element: <ServiceRequestsPage /> },
        { path: "settings",          element: <SettingsPage /> },
        { path: "profile",          element: <AdminProfilePage /> },
        { path: "notifications",    element: <NotificationsPage /> },
        { path: "legal",            element: <AdminLegalListPage /> },
        { path: "legal/:id",        element: <AdminLegalEditPage /> },
      ],
    },

    // ── Authenticated publisher routes — AppShell via AuthGuard ─────────────────
    {
      element: <AuthGuard />,
      children: [
        { path: "/notifications",          element: <NotificationsPage /> },
        { path: "/settings/profile",       element: <PublisherProfilePage /> },
        { path: "/change-password",        element: <ChangePasswordPage /> },
        { path: "/dashboard",              element: <PublisherDashboardPage /> },
        { path: "/billing",                element: <BillingDashboardPage /> },
        { path: "/dashboard/settings",     element: <SettingsPage /> },
        { path: "/services",               element: <ServiceRequestsPage /> },
        { path: "/publisher/titles",        element: <TitleListPage /> },
        { path: "/publisher/titles/new",   element: <TitleWizardPage /> },
        { path: "/publisher/titles/:id",   element: <PublisherTitleDetailPage /> },
        { path: "/publisher/titles/:id/revision", element: <PublisherTitleRevisionPage /> },
      ],
    },

    // ── Error pages — standalone, no shell (sidebar-free 404/500) ─────────
    { path: "/500", element: <ErrorPage /> },
    {
      path: "*",
      element: <NotFoundPage />,
      errorElement: <RootErrorElement />,
      loader: () => { throw new Response(null, { status: 404 }); },
    },

    // ── Root redirect ───────────────────────────────────────────────────────
    { path: "/", element: <Navigate to="/login" replace /> },
  ],
  { basename },
);

/**
 * AppRouter — root provider tree and router entry point.
 */
/** Route guard aliases for test compatibility */
export const GuestRoute = GuestGuard;
export const AuthRoute = AuthGuard;
export const AdminRoute = AdminGuardLayout;

export function AppRouter() {
  return (
    <QueryClientProvider client={queryClient}>
      <DirectionProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </DirectionProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
